import PropTypes from 'prop-types';
import { List, ListSubheader } from '@mui/material';
import { DashboardSidebarItem } from './DashboardSidebarItem';
import { MenuItemT } from 'types/frontend/Menu';
import type { FC } from 'react';
import type { ListProps } from '@mui/material';

interface DashboardSidebarSectionProps extends ListProps {
  items: MenuItemT[];
  path: string;
  title: string;
}

const renderNavItems = ({
  depth = 0,
  items,
  path,
}: {
  depth?: number;
  items: MenuItemT[];
  path: string;
}): JSX.Element => (
  <List disablePadding>
    {items.reduce(
      (acc: JSX.Element[], item) =>
        reduceChildRoutes({
          acc,
          item,
          depth,
          path,
        }),
      [],
    )}
  </List>
);

const reduceChildRoutes = ({
  acc,
  item,
  depth,
  path,
}: {
  acc: JSX.Element[];
  depth: number;
  item: MenuItemT;
  path: string;
}): Array<JSX.Element> => {
  const key = `${item.title}-${depth}`;
  // const partialMatch = path.includes(item.path || '');
  const partialMatch = false;
  const exactMatch = path === item.path;

  if (item.children) {
    acc.push(
      <DashboardSidebarItem
        active={partialMatch}
        chip={item.chip}
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={partialMatch}
        path={item.path}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          items: item.children,
          path,
        })}
      </DashboardSidebarItem>,
    );
  } else {
    acc.push(
      <DashboardSidebarItem
        active={exactMatch}
        chip={item.chip}
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        path={item.path}
        title={item.title}
        newUntil={item.newUntil}
      />,
    );
  }

  return acc;
};

export const DashboardSidebarSection: FC<DashboardSidebarSectionProps> = (
  props,
) => {
  const { items, path, title, ...other } = props;

  return (
    <List
      subheader={
        <ListSubheader
          disableGutters
          disableSticky
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 700,
            lineHeight: 2.5,
            ml: 4,
            textTransform: 'uppercase',
          }}
        >
          {title}
        </ListSubheader>
      }
      {...other}
    >
      {renderNavItems({
        items,
        path,
      })}
    </List>
  );
};

DashboardSidebarSection.propTypes = {
  items: PropTypes.array.isRequired,
  path: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};
